import { useCallback, useEffect, useMemo, useState } from 'react';
import { completedElsewhereExport, completedElsewhereReport } from '@lib/care-api';
import { hayIncludes } from '@lib/hay';
import { inCampaign, useCampaign } from '@lib/campaigns';
import { CampaignScopeNote } from '@components/CampaignScopeNote';
import { fmtMemberId } from '@lib/member-id';
import { eventPageUrl } from '@lib/open-mode';
import { CareGrid } from '@components/grid/CareGrid';
import { useGridView, type GridColumn } from '@components/grid/useGridView';
import type { SlicerDef } from '@lib/grid';
import {
  CAMPAIGN_META,
  NO_CAMPAIGN_WORDING,
  STATUS_META,
  fmtPhone,
  fmtTs,
  type CompletedElsewhereResp,
  type CompletedElsewhereRow,
  type CompletedElsewhereVia,
} from '@lib/care-model';

/**
 * Reports (nav 09, Alignment feedback 2026-08) - reports built FOR the client off the worked queue.
 * First report: "Completed via another method" - every member who indicated the screening is already
 * done outside the CDX kit, so Alignment can review and update their records. Rows come from the
 * three structured captures (the test-completed disposition, the item-6 member request, the opt-out
 * reason), never from free-text mining; the drawer is where those get recorded.
 *
 * Read-only. The Excel export is reason-gated + watermarked + audited server-side, exactly like the
 * Lookup roster export.
 */

const VIA_PILL: Record<CompletedElsewhereVia, string> = {
  disposition: 'pill-success',
  'member-request': 'pill-primary',
  'opt-out-reason': 'pill-warning',
};

const VIA_SHORT: Record<CompletedElsewhereVia, string> = {
  disposition: 'Disposition',
  'member-request': 'Member request',
  'opt-out-reason': 'Opt-out reason',
};

const nowrap = { whiteSpace: 'nowrap' } as const;
const dash = <span className="muted">-</span>;

/**
 * Row 356 (approval 169): the report's table through the shared grid. The first nine columns are the
 * report as it was; Campaign, Language, Status and Insurance ID are extras she can tick on in Columns.
 */
const REPORT_COLUMNS: GridColumn<CompletedElsewhereRow>[] = [
  { key: 'recorded', label: 'Recorded', tdStyle: nowrap, firstDir: 'desc', sortValue: (r) => Date.parse(r.recordedTs || r.receivedTs) || null, render: (r) => fmtTs(r.recordedTs || r.receivedTs) },
  {
    key: 'member', label: 'Member', sortValue: (r) => r.memberName,
    render: (r) => (
      <>
        {r.memberName || <span className="muted">Unmatched</span>}
        {r.memberId && <span className="muted" style={{ marginLeft: 6 }}>#{fmtMemberId(r.memberId)}</span>}
      </>
    ),
  },
  { key: 'dob', label: 'DOB', tdStyle: nowrap, sortValue: (r) => r.dob, render: (r) => r.dob || dash },
  { key: 'phone', label: 'Phone', tdStyle: nowrap, sortValue: (r) => r.phone, render: (r) => (r.phone ? fmtPhone(r.phone) : dash) },
  {
    key: 'signal', label: 'Signal', sortValue: (r) => VIA_SHORT[r.via] ?? r.via,
    render: (r) => (
      <span className={`pill ${VIA_PILL[r.via] ?? ''}`} title={r.viaLabel}>
        {VIA_SHORT[r.via] ?? r.via}
      </span>
    ),
  },
  { key: 'recordedBy', label: 'Recorded by', sortValue: (r) => r.recordedBy, render: (r) => r.recordedBy || dash },
  { key: 'channel', label: 'Channel', sortValue: (r) => r.channel, render: (r) => r.channel },
  {
    key: 'note', label: 'Note', sortValue: (r) => r.note,
    tdStyle: { maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    render: (r) => <span title={r.note}>{r.note || dash}</span>,
  },
  {
    key: 'event', label: 'Event', tdStyle: nowrap,
    // The pop-out single-event window (same one the drawer's "Pop out" opens).
    render: (r) => (
      <a href={eventPageUrl(r.eventId)} target="_blank" rel="noopener noreferrer">
        Open
      </a>
    ),
  },
  {
    key: 'campaign', label: 'Campaign', offByDefault: true, tdStyle: nowrap,
    sortValue: (r) => (r.campaign ? CAMPAIGN_META[r.campaign]?.label ?? r.campaign : ''),
    render: (r) => (r.campaign ? CAMPAIGN_META[r.campaign]?.label ?? r.campaign : <span className="muted">{NO_CAMPAIGN_WORDING.cell}</span>),
  },
  { key: 'language', label: 'Language', offByDefault: true, sortValue: (r) => r.language, render: (r) => r.language || dash },
  {
    key: 'status', label: 'Status', offByDefault: true, sortValue: (r) => (STATUS_META as Record<string, { label: string }>)[r.status]?.label ?? r.status,
    render: (r) => (STATUS_META as Record<string, { label: string }>)[r.status]?.label ?? r.status,
  },
  { key: 'insuranceId', label: 'Insurance ID', offByDefault: true, tdStyle: nowrap, sortValue: (r) => r.insuranceId, render: (r) => r.insuranceId || dash },
];

const REPORT_SLICERS: SlicerDef<CompletedElsewhereRow>[] = [
  { key: 'signal', label: 'Signal', value: (r) => r.via, labelFor: (v) => VIA_SHORT[v as CompletedElsewhereVia] ?? v },
  { key: 'channel', label: 'Channel', value: (r) => r.channel },
  { key: 'recordedBy', label: 'Recorded by', value: (r) => r.recordedBy, blankLabel: 'Not recorded' },
  { key: 'campaign', label: 'Campaign', value: (r) => r.campaign, blankLabel: NO_CAMPAIGN_WORDING.chip, labelFor: (v) => CAMPAIGN_META[v]?.label ?? v },
  { key: 'language', label: 'Language', value: (r) => r.language, blankLabel: 'Not known' },
];

export function Reports() {
  const [resp, setResp] = useState<CompletedElsewhereResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');

  const [exportOpen, setExportOpen] = useState(false);
  const [exportReason, setExportReason] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportErr, setExportErr] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    completedElsewhereReport()
      .then((r) => setResp(r))
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : 'Could not load the report.');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // ROW 354: the report follows the top-bar campaign on SCREEN; a row naming no campaign always stays.
  // The Excel export stays the whole client, and the note says so.
  const topCampaign = useCampaign();
  const loadedRows = resp?.rows ?? [];
  const rows = useMemo(() => loadedRows.filter((r) => inCampaign(r.campaign, topCampaign)), [loadedRows, topCampaign]);
  const setAside = loadedRows.length - rows.length;
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) => {
      const hay =
        `${r.memberName} ${r.memberId} ${r.phone} ${r.recordedBy} ${r.viaLabel} ${r.channel} ${r.note} ${r.eventId}`.toLowerCase();
      return hayIncludes(hay, needle);
    });
  }, [rows, q]);

  const view = useGridView('report-completed-elsewhere', REPORT_COLUMNS, { defaultSort: [], slicers: REPORT_SLICERS });

  const runExport = async () => {
    setExporting(true);
    setExportErr('');
    try {
      const { blob, filename } = await completedElsewhereExport(exportReason.trim());
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
      setExportOpen(false);
    } catch (e) {
      setExportErr(e instanceof Error ? e.message : 'Export failed.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="page-pad">
      <header>
        <div className="section-eyebrow">Member Care &middot; Reports</div>
        <h1 className="section-title">Completed via another method</h1>
        <div className="section-rule" />
        <p className="section-lede">
          Members who indicated the screening is already completed outside the CDX kit (their own
          doctor&apos;s test, a colonoscopy, a Cologuard), for Alignment to review and update their
          records. One row per event carrying a structured capture: the Test-completed disposition,
          the member-request log, or an opt-out recorded with reason &quot;Already completed the
          test&quot;.{' '}
          {resp
            ? `${resp.count} indication${resp.count === 1 ? '' : 's'} from ${resp.memberCount} member${resp.memberCount === 1 ? '' : 's'}.`
            : ''}
        </p>
      </header>
      <CampaignScopeNote others={setAside} />
      {topCampaign !== 'all' && <div className="micro muted" style={{ marginBottom: 8 }}>The Excel export holds every campaign of the client.</div>}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <button type="button" className="btn btn-ghost btn-sm" onClick={load} disabled={loading}>
          Refresh
        </button>
        <button
          type="button"
          className="btn btn-sm"
          disabled={exporting || loading || !loadedRows.length}
          onClick={() => {
            setExportErr('');
            setExportOpen((v) => !v);
          }}
        >
          {exporting ? 'Exporting...' : 'Export to Excel'}
        </button>
        <input
          className="text-input"
          type="search"
          aria-label="Search the report"
          style={{ marginLeft: 'auto', maxWidth: 340 }}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name / member ID / phone / note"
        />
      </div>

      {exportOpen && (
        <div className="note" style={{ marginBottom: 14 }}>
          <b>Export {loadedRows.length.toLocaleString()} row{loadedRows.length === 1 ? '' : 's'}</b> to an
          audited Excel workbook for Alignment. The export is its own PHI disclosure and needs a
          reason.
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            <input
              className="text-input"
              aria-label="Export reason"
              style={{ flex: '1 1 260px' }}
              value={exportReason}
              onChange={(e) => setExportReason(e.target.value)}
              placeholder="Reason for the download (min 4 characters)"
            />
            <button
              type="button"
              className="btn btn-sm"
              disabled={exporting || exportReason.trim().length < 4}
              onClick={() => void runExport()}
            >
              Download
            </button>
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => setExportOpen(false)}>
              Cancel
            </button>
          </div>
          {exportErr && <div style={{ color: 'var(--danger)', marginTop: 6 }}>{exportErr}</div>}
        </div>
      )}

      {error && <div className="note" style={{ marginBottom: 14 }}>{error}</div>}
      {resp && !resp.archiveCovered && (
        <div className="note" style={{ marginBottom: 14 }}>
          member_care.db was unavailable for this read, so archived (cleared) events are missing -
          this view covers the live queue only. Refresh to retry.
        </div>
      )}

      {loading && !resp ? (
        <div className="muted">Loading&hellip;</div>
      ) : !loadedRows.length ? (
        <div className="muted">
          No completed-elsewhere indications recorded yet. They appear here once an operator records
          the Test-completed disposition (Completed via another method), the matching member request,
          or an opt-out with reason &quot;Already completed the test&quot;.
        </div>
      ) : (
        <CareGrid
          view={view}
          rows={shown}
          rowKey={(r) => r.eventId}
          noun="members"
          empty="Nothing matches this search."
        />
      )}
    </div>
  );
}
